import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { ChevronLeft, Calendar, Loader, FileText, Flame, Target, ChevronRight, Database as DbIcon, Play } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';
import apiService from '../../services/apiService';

const Page = styled.div`
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  background: #0a0a0a;
  color: #fff;
  font-family: 'Inter', sans-serif;
`;

const Header = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #222;
  background: #141414;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const BackBtn = styled(Link)`
  color: #888;
  display: flex;
  align-items: center;
  text-decoration: none;
  font-size: 14px;
  &:hover { color: #fff; }
`;

const Title = styled.h2`
  font-size: 18px;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Layout = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  @media (max-width: 768px) {
    flex-direction: column;
    overflow-y: auto;
  }
`;

const Sidebar = styled.div`
  width: 320px;
  border-right: 1px solid #222;
  display: flex;
  flex-direction: column;
  background: #111;
  @media (max-width: 768px) {
    width: 100%;
    display: ${p => p.$hiddenOnMobile ? 'none' : 'flex'};
    border-right: none;
  }
`;

const ListHeader = styled.div`
  padding: 12px 16px;
  font-size: 13px;
  color: #666;
  border-bottom: 1px solid #222;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const SnapshotList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SnapshotItem = styled.div`
  padding: 16px;
  border-bottom: 1px solid #1a1a1a;
  cursor: pointer;
  background: ${p => p.$active ? 'rgba(229, 9, 20, 0.1)' : 'transparent'};
  border-left: 3px solid ${p => p.$active ? '#e50914' : 'transparent'};
  &:hover {
    background: ${p => p.$active ? 'rgba(229, 9, 20, 0.1)' : '#1a1a1a'};
  }
`;

const SnapTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  margin-bottom: 4px;
  color: ${p => p.$active ? '#fff' : '#ccc'};
`;

const SnapMeta = styled.div`
  font-size: 11px;
  color: #666;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const MoodTag = styled.span`
    padding: 2px 6px;
    border-radius: 4px;
    background: ${p => p.$mood === 'HOT' ? 'rgba(229, 9, 20, 0.2)' : 'rgba(255,255,255,0.05)'};
    color: ${p => p.$mood === 'HOT' ? '#e50914' : '#888'};
    font-size: 10px;
    font-weight: 800;
`;

const MainContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 30px;
  background: #0a0a0a;
  @media (max-width: 768px) {
    padding: 20px 15px;
    display: ${p => p.$visibleOnMobile ? 'block' : 'none'};
  }
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  margin: 30px 0 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  color: #fff;
  &:first-child { margin-top: 0; }
  @media (max-width: 768px) {
    font-size: 15px;
    margin: 24px 0 12px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 14px;
  @media (max-width: 768px) {
    display: none; // Use Cards on mobile
  }
  th, td {
    padding: 12px;
    border-bottom: 1px solid #1a1a1a;
    text-align: right;
  }
  th {
      color: #666;
      font-size: 12px;
      font-weight: 500;
      background: #111;
      text-align: right;
  }
  th:first-child, td:first-child { text-align: left; }
  th:nth-child(2), td:nth-child(2) { text-align: left; }
`;

const SyncingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.85);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
`;

const spin = keyframes`
  100% { transform: rotate(360deg); }
`;

const Spinner = styled(Loader)`
  animation: ${spin} 1s linear infinite;
  margin-bottom: 20px;
`;

const LeaderCard = styled.div`
  display: none;
  background: #111;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 14px;
  margin-bottom: 10px;
  @media (max-width: 768px) {
    display: block;
  }
`;

const PivotCard = styled.div`
  background: #141414;
  border: 1px solid #222;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  &:hover { border-color: #e50914; }
`;

const StatusTag = styled.span`
  font-size: 10px;
  font-weight: 800;
  padding: 2px 6px;
  border-radius: 4px;
  background: ${props => props.$status === 'BREAKOUT' ? '#e50914' : props.$status === 'NEAR_BREAKOUT' ? '#f59e0b' : '#333'};
  color: #fff;
`;

export default function LivermoreHistoryPage() {
    const navigate = useNavigate();
    const [snapshots, setSnapshots] = useState([]);
    const [selectedSnap, setSelectedSnap] = useState(null);
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [isSyncing, setIsSyncing] = useState(false);
    const [syncProgress, setSyncProgress] = useState(null);
    const [syncMessage, setSyncMessage] = useState('');

    const handleRecalculate = async () => {
        if (!selectedSnap) {
            alert('먼저 분석할 대상을 선택해 주세요.');
            return;
        }

        const confirmed = window.confirm('현재 스냅샷의 성과를 다시 계산하시겠습니까?\n(분봉 데이터가 부족한 경우 KIS에서 다시 조회합니다)');
        if (!confirmed) return;
        
        setIsSyncing(true);
        setSyncMessage('성과 재계산 중...');
        setSyncProgress(null);
        await new Promise(resolve => setTimeout(resolve, 50)); // React가 오버레이를 먼저 렌더링하도록 yield
        try {
            console.log(`[History] Forcing performance re-calculation for ${selectedSnap.id}...`);
            const updated = await apiService.calculateLivermorePerformance(selectedSnap.id, true);
            if (updated.success) {
                setDetails(prev => ({ 
                    ...prev, 
                    pivotPoints: updated.pivotPoints,
                    leaders: updated.leaders
                }));
                alert('성과 재계산이 완료되었습니다.');
            }
        } catch (err) {
            console.error('[History] Recalculate failed:', err);
            alert('재계산 실패: ' + err.message);
        } finally {
            setIsSyncing(false);
        }
    };

    const handleNavigateVwap = () => {
        if (!selectedSnap || !details) return;
        
        const date = new Intl.DateTimeFormat('ko-KR', {
            year: 'numeric', month: '2-digit', day: '2-digit'
        }).format(new Date(details.createdAt)).replace(/. /g, '').replace('.', '');
        
        const codes = (details.pivotPoints || []).map(p => p.code).join(',');
        const names = (details.pivotPoints || []).map(p => p.name).join(',');
        
        const params = new URLSearchParams({
            mode: 'simulate',
            date: date,
            stocks: codes,
            names: names
        });
        
        navigate(`/labs/vwap?${params.toString()}`);
    };

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    useEffect(() => {
        const loadHistory = async () => {
            try {
                const data = await apiService.getLivermoreHistory();
                setSnapshots(data || []);
                if (data && data.length > 0) {
                    handleSelect(data[0]);
                }
            } catch (err) {
                console.error('Failed to load history', err);
            } finally {
                setLoading(false);
            }
        };
        loadHistory();
    }, []);

    const handleSelect = async (snap) => {
        setSelectedSnap(snap);
        setDetailsLoading(true);
        setDetails(null);
        try {
            const data = await apiService.getLivermoreHistoryDetail(snap.id);
            setDetails(data);

            // JIT Performance Calculation Check
            // 재계산 조건: 아직 계산 안 됐거나, 데이터가 불완전('-')한 경우
            const firstPivot = data.pivotPoints?.[0];
            const firstLeader = data.leaders?.[0];
            
            // 어느 하나라도 성능 데이터가 없거나 '-'이면 계산 시도 (전수 조사)
            const needsCalc = (data.pivotPoints?.some(p => !p.performance || p.performance.todayMax === '-' || p.performance.nextDayMax === '-')) || 
                              (data.leaders?.some(l => !l.performance || l.performance.todayMax === '-' || l.performance.nextDayMax === '-'));

            if (data && needsCalc) {
                const createdAt = new Date(data.createdAt);
                const nextDay = new Date(createdAt);
                nextDay.setDate(nextDay.getDate() + 1);
                nextDay.setHours(15, 30, 0, 0);

                if (new Date() > nextDay) {
                    console.log('[History] Triggering JIT performance calculation...');
                    const updated = await apiService.calculateLivermorePerformance(snap.id);
                    if (updated.success) {
                        setDetails(prev => ({ 
                            ...prev, 
                            pivotPoints: updated.pivotPoints,
                            leaders: updated.leaders
                        }));
                    }
                }
            }
        } catch (err) {
            console.error('Failed to load snapshot details', err);
        } finally {
            setDetailsLoading(false);
        }
    };

    const PerformanceBadge = ({ label, value }) => {
        if (!value) return null;
        const isNeutral = value === '-';
        const isPositive = value.includes('+');
        const color = isNeutral ? '#444' : (isPositive ? '#ef4444' : '#3b82f6');
        
        return (
            <div style={{ 
                display: 'flex', flexDirection: 'column', alignItems: 'flex-end', 
                background: isNeutral ? 'rgba(255,255,255,0.03)' : (isPositive ? 'rgba(239, 68, 68, 0.05)' : 'rgba(59, 130, 246, 0.05)'),
                padding: '4px 8px', borderRadius: '6px', border: `1px solid ${isNeutral ? '#222' : (isPositive ? 'rgba(239, 68, 68, 0.2)' : 'rgba(59, 130, 246, 0.2)')}`
            }}>
                <span style={{ fontSize: '9px', color: '#666', textTransform: 'uppercase' }}>{label}</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color }}>{value}</span>
            </div>
        );
    };

    const formatTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleString('ko-KR', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <Page>
            {isSyncing && (
                <SyncingOverlay>
                    <Spinner size={48} color="#e50914" />
                    <h2 style={{ margin: 0, marginBottom: '8px' }}>{syncMessage || 'Syncing Minute Data'}</h2>
                    {syncProgress && (
                        <>
                            <p style={{ margin: 0, color: '#aaa', fontSize: '16px', fontWeight: 600 }}>
                                {syncProgress.current} / {syncProgress.total} 
                                {syncProgress.total > 0 && ` (${Math.round((syncProgress.current / syncProgress.total) * 100)}%)`}
                            </p>
                            <p style={{ margin: '8px 0 0', color: '#666', fontSize: '13px' }}>
                                Processing: <span style={{ color: '#fff' }}>{syncProgress.name}</span>
                            </p>
                            <div style={{ width: '200px', height: '4px', background: '#333', marginTop: '20px', borderRadius: '2px', overflow: 'hidden' }}>
                                <div style={{ 
                                    width: `${(syncProgress.current / syncProgress.total) * 100}%`, 
                                    height: '100%', 
                                    background: '#e50914',
                                    transition: 'width 0.3s ease'
                                }} />
                            </div>
                        </>
                    )}
                    {!syncProgress && (
                        <p style={{ margin: 0, color: '#aaa', fontSize: '14px' }}>
                            KIS 분봉 데이터를 조회하여 성과를 계산합니다...
                        </p>
                    )}
                </SyncingOverlay>
            )}
            <Header>
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    {isMobile && selectedSnap ? (
                        <button 
                            onClick={() => setSelectedSnap(null)}
                            style={{ background: 'none', border: 'none', color: '#e50914', display: 'flex', alignItems: 'center', padding: 0 }}
                        >
                            <ChevronLeft size={20} /> List
                        </button>
                    ) : (
                        <BackBtn to="/labs/livermore"><ChevronLeft size={18} /> Back</BackBtn>
                    )}
                    <Title><Calendar size={18} color="#e50914" /> {isMobile ? 'History' : 'Livermore Analysis History'}</Title>
                </div>
                <button 
                    onClick={handleRecalculate} 
                    disabled={isSyncing || !selectedSnap}
                    style={{ 
                        background: selectedSnap ? '#e50914' : '#333', 
                        color: '#fff', border: 'none', 
                        padding: '6px 16px', borderRadius: '8px', fontSize: '13px', 
                        fontWeight: 700, cursor: (isSyncing || !selectedSnap) ? 'not-allowed' : 'pointer',
                        display: 'flex', alignItems: 'center', gap: '8px',
                        boxShadow: selectedSnap ? '0 4px 12px rgba(229, 9, 20, 0.3)' : 'none'
                    }}
                >
                    {isSyncing ? <Spinner size={14} style={{ margin: 0 }} /> : <Flame size={14} />}
                    {isSyncing ? '계산 중...' : '다시 분석하기'}
                </button>
                {selectedSnap && details && (
                    <button 
                        onClick={handleNavigateVwap}
                        style={{ 
                            background: 'linear-gradient(135deg, #f43f5e, #fb923c)',
                            color: '#fff', border: 'none', 
                            padding: '6px 16px', borderRadius: '8px', fontSize: '13px', 
                            fontWeight: 700, cursor: 'pointer',
                            display: 'flex', alignItems: 'center', gap: '8px',
                            boxShadow: '0 4px 12px rgba(244, 63, 94, 0.3)'
                        }}
                    >
                        <Play size={14} fill="#fff" />
                        [wap 가상매매]
                    </button>
                )}
            </Header>
            <Layout>
                <Sidebar $hiddenOnMobile={isMobile && selectedSnap}>
                    <ListHeader>Recorded Snapshots</ListHeader>
                    {loading ? (
                        <div style={{ padding: 40, textAlign: 'center' }}><Loader className="animate-spin" size={20} /></div>
                    ) : (
                        <SnapshotList>
                            {snapshots.map(s => (
                                <SnapshotItem 
                                    key={s.id} 
                                    $active={selectedSnap?.id === s.id}
                                    onClick={() => handleSelect(s)}
                                >
                                    <SnapTitle $active={selectedSnap?.id === s.id}>
                                        {formatTime(s.createdAt)} Analysis
                                    </SnapTitle>
                                    <SnapMeta>
                                        <MoodTag $mood={s.marketMood}>{s.marketMood}</MoodTag>
                                        <span>{s.leaderCount}L | {s.pivotCount}P</span>
                                    </SnapMeta>
                                </SnapshotItem>
                            ))}
                            {snapshots.length === 0 && <div style={{ padding: 20, color: '#444', textAlign: 'center', fontSize: '13px' }}>No records found.</div>}
                        </SnapshotList>
                    )}
                </Sidebar>
                <MainContent $visibleOnMobile={!isMobile || selectedSnap}>
                    {detailsLoading ? (
                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
                            <Loader className="animate-spin" size={32} color="#e50914" />
                        </div>
                    ) : details ? (
                        <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid #222', paddingBottom: '20px', marginBottom: '30px' }}>
                                <div>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                        <h2 style={{ margin: '0 0 8px', fontSize: isMobile ? '20px' : '24px' }}>{formatTime(details.createdAt)}</h2>
                                    </div>
                                    <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
                                        Market Mood: <span style={{ color: details.marketMood === 'HOT' ? '#e50914' : '#fff', fontWeight: 700 }}>{details.marketMood}</span>
                                    </p>
                                </div>
                                {!isMobile && (
                                    <div style={{ textAlign: 'right' }}>
                                        <div style={{ fontSize: '11px', color: '#444', textTransform: 'uppercase', marginBottom: '4px' }}>Original Timestamp</div>
                                        <div style={{ fontSize: '13px', color: '#888' }}>{new Date(details.timestamp).toLocaleString()}</div>
                                    </div>
                                )}
                            </div>

                            <SectionTitle><Target size={20} color="#34d399" /> Identified Pivot Points ({details.pivotPoints?.length || 0})</SectionTitle>
                            {details.pivotPoints?.length === 0 ? (
                                <p style={{ color: '#444', fontSize: '14px', fontStyle: 'italic' }}>No pivot points detected at this time.</p>
                            ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' }}>
                                    {details.pivotPoints.map(item => (
                                        <PivotCard 
                                            key={item.code} 
                                            onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}&pivot=${item.resistanceLine}`, '_blank')}
                                        >
                                            <div>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                                    <span style={{ fontWeight: 700 }}>{item.name}</span>
                                                    <span style={{ fontSize: '13px', color: '#a1a1aa' }}>{item.price?.toLocaleString()}원</span>
                                                    <StatusTag $status={item.pivotStatus}>{item.pivotStatus}</StatusTag>
                                                    {item.isTrendStarter && (
                                                        <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold' }}>
                                                            👑장기추세 초입
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '11px', color: '#666', marginTop: 4 }}>
                                                    Vol Spike: {item.volSurge?.toFixed(1)}x | Volatility: {item.volatility}
                                                    {item.trValueFormatted && <span style={{marginLeft: 8}}> | 대금: {item.trValueFormatted} | 시총: {item.mcapFormatted} </span>}
                                                    {item.targetUpside && <span style={{marginLeft: 8, color: '#10b981', fontWeight: 600}}>🎯상승여력: {item.targetUpside}</span>}
                                                </div>
                                            </div>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                {item.performance && (
                                                    <>
                                                        <PerformanceBadge label="Today Max" value={item.performance.todayMax} />
                                                        <PerformanceBadge label="Next Day Max" value={item.performance.nextDayMax} />
                                                    </>
                                                )}
                                                <ChevronRight size={18} color="#333" />
                                            </div>
                                        </PivotCard>
                                    ))}
                                </div>
                            )}

                            <SectionTitle><Flame size={20} color="#e50914" /> Top Leading Stocks</SectionTitle>
                            
                            {/* Desktop Table */}
                            <Table>
                                <thead>
                                    <tr>
                                        <th>Rank</th>
                                        <th>Name</th>
                                        <th style={{ display: isMobile ? 'none' : 'table-cell' }}>Symbol</th>
                                        <th>Price</th>
                                        <th>Change</th>
                                        <th>Today</th>
                                        <th>Next</th>
                                        <th>Score</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {details.leaders.slice(0, 20).map((item, idx) => (
                                        <tr key={item.code} onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}`, '_blank')} style={{ cursor: 'pointer' }}>
                                            <td style={{ color: '#444' }}>#{idx + 1}</td>
                                            <td style={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                {item.name}
                                                {item.isTrendStarter && (
                                                    <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold' }}>
                                                        👑초입
                                                    </span>
                                                )}
                                            </td>
                                            <td style={{ fontSize: '12px', color: '#666', display: isMobile ? 'none' : 'table-cell' }}>{item.code}</td>
                                            <td>{item.price.toLocaleString()}</td>
                                            <td style={{ color: item.changeRate > 0 ? '#ef4444' : '#3b82f6' }}>
                                                {item.changeRate > 0 ? '+' : ''}{item.changeRate}%
                                            </td>
                                            <td><span style={{ color: (item.performance?.todayMax || '-').includes('+') ? '#ef4444' : (item.performance?.todayMax === '-' ? '#444' : '#3b82f6'), fontWeight: 700 }}>{item.performance?.todayMax || '-'}</span></td>
                                            <td><span style={{ color: (item.performance?.nextDayMax || '-').includes('+') ? '#ef4444' : (item.performance?.nextDayMax === '-' ? '#444' : '#3b82f6'), fontWeight: 700 }}>{item.performance?.nextDayMax || '-'}</span></td>
                                            <td style={{ fontWeight: 700, color: '#f59e0b' }}>{item.totalScore.toFixed(0)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>

                                      {details.leaders.slice(0, 20).map((item, idx) => (
                                <LeaderCard key={item.code} onClick={() => window.open(`/labs/stock/technical?symbol=${item.code}`, '_blank')}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                                        <div style={{ fontWeight: 700, fontSize: '15px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {idx + 1}. {item.name}
                                            {item.isTrendStarter && (
                                                <span style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', padding: '2px 4px', borderRadius: '4px', fontSize: '9px', fontWeight: 'bold', verticalAlign: 'text-top' }}>👑초입</span>
                                            )}
                                        </div>
                                        <div style={{ display: 'flex', gap: '8px' }}>
                                            {item.performance && (
                                                <>
                                                    <span style={{ fontSize: '11px', color: item.performance.todayMax.includes('+') ? '#ef4444' : '#666' }}>T:{item.performance.todayMax}</span>
                                                    <span style={{ fontSize: '11px', color: item.performance.nextDayMax.includes('+') ? '#ef4444' : '#666' }}>N:{item.performance.nextDayMax}</span>
                                                </>
                                            )}
                                            <div style={{ fontSize: '14px', fontWeight: 700, color: item.changeRate > 0 ? '#ef4444' : '#3b82f6' }}>
                                                {item.changeRate > 0 ? '+' : ''}{item.changeRate}%
                                            </div>
                                        </div>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', color: '#888', fontSize: '12px' }}>
                                        <div>{item.code} | {item.price.toLocaleString()}원</div>
                                        <div style={{ color: '#f59e0b', fontWeight: 700 }}>{item.totalScore.toFixed(0)}pts</div>
                                    </div>
                                </LeaderCard>
                            ))}
                        </div>
                    ) : (
                        <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: '#444' }}>
                            Select a snapshot from the sidebar to view details.
                        </div>
                    )}
                </MainContent>
            </Layout>
        </Page>
    );
}
